import React from 'react';
import { isValid, isBefore } from 'date-fns';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isUnauthenticatedError } from '@confluence/error-boundary/entry-points/isUnauthenticatedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { useSiteUserTraits } from './useSiteUserTraits.ts';
import { useShouldExcludeUser } from './useShouldExcludeUser.ts';
import { ExcludeHandler } from './ExcludeHandler.tsx';

type ExcludeNewConfluenceActiveUserProps = {
	activeDaysThreshold?: number; // default to exclude users who have not been active for 28 days
	activeUserCutoffDate?: Date; // when this date is provided, users whose firstActive is AFTER this date are excluded (they are new users who don't need changeboarding)
	excludeEmbeddedConfluence?: boolean; // when true, only consider the confluence_first_active trait for logic
	/**
	 * Optional callback fired when the user is excluded by this component.
	 * Useful for performing side-effects such as analytics, logging, or marking exclusion in state.
	 */
	onExclude?: () => void;
	children: React.ReactNode;
};

const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';
const EMBEDDED_CONFLUENCE_FIRST_ACTIVE_TRAIT = 'embedded_confluence_user_first_active';

export const ExcludeNewConfluenceActiveUser = ({
	children,
	activeDaysThreshold = 28,
	activeUserCutoffDate,
	excludeEmbeddedConfluence = false,
	onExclude,
}: ExcludeNewConfluenceActiveUserProps) => {
	const { data, loading, error } = useSiteUserTraits();

	const attributes = data?.attributes ?? null;
	const confluenceFirstActive = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;
	const embeddedConfluenceFirstActive = attributes?.find(
		({ name }: { name: string }) => name === EMBEDDED_CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActive = excludeEmbeddedConfluence
		? confluenceFirstActive
		: getEarliestDate(confluenceFirstActive, embeddedConfluenceFirstActive);

	const firstActiveDate = firstActive ? new Date(firstActive) : null;

	const { shouldExclude } = useShouldExcludeUser({
		checkDate: firstActiveDate,
		daysThreshold: activeDaysThreshold,
		cutoffDate: activeUserCutoffDate,
	});

	if (loading) {
		return null;
	}

	if (error) {
		if (isUnauthorizedError(error) || isUnauthenticatedError(error)) {
			markErrorAsHandled(error);
			return null;
		}

		return <ErrorDisplay error={error} />;
	}

	if (shouldExclude) {
		return onExclude ? <ExcludeHandler onExclude={onExclude} /> : null;
	}

	return <>{children}</>;
};

/**
 * Returns the earliest valid date from two date strings, or null if neither is valid
 */
const getEarliestDate = (date1?: string, date2?: string): string | null => {
	const validDates = [date1, date2]
		.filter(Boolean)
		.map((date) => new Date(date || ''))
		.filter(isValid);

	if (validDates.length === 0) {
		return null;
	}

	if (validDates.length === 1) {
		return validDates[0].toISOString();
	}

	return isBefore(validDates[0], validDates[1])
		? validDates[0].toISOString()
		: validDates[1].toISOString();
};
